import React, { useState, useMemo, useEffect } from 'react';
import { useGetOrganizationQuery, useUploadSourceFileMutation, useGetUploadedFilesInOrganizationQuery, useGetSourceFileUploadHistoryMonthsQuery, useGetAugmentationFilesInOrganizationQuery, useUploadAugmentationFileMutation } from '../../../../api/organizationSlice';
import { Stack, Typography, Alert, CircularProgress, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useParams } from 'react-router';
import ExpectedFilesList, { ORG_EXPECTED_FILES_WITH_HEADERS } from './ExpectedFilesList';
import FileGroupingCard from './FileGroupingCard';
import FileUploadSection from './FileUploadSection';
import TransformationJobs from './TransformationJobs';
import { handleFileChange, FileType, validateFileDate } from './Controllers/fileHandlers';

// Allina is the only organization currently allowed to upload source files.
// Keep this as a named constant so the exception is obvious and easy to update.
const ALLINA_ORGANIZATION_ID = '2';


function UploadSourceFile() {
    const { organizationId } = useParams();
    const { data: organization } = useGetOrganizationQuery(organizationId);
    const canUploadSourceFiles = organizationId === ALLINA_ORGANIZATION_ID;
    
    // Get current month in YYYY-MM format and readable format
    const currentDate = new Date();
    const currentMonth = currentDate.toISOString().slice(0, 7);
    const monthName = currentDate.toLocaleDateString('en-US', { month: 'long' });
    const year = currentDate.getFullYear();
    const {
        data: sourceHistoryMonths = [],
        isLoading: isLoadingSourceHistoryMonths,
    } = useGetSourceFileUploadHistoryMonthsQuery(organizationId);
    const [selectedSourceMonth, setSelectedSourceMonth] = useState('');

    const sortedSourceHistoryMonths = useMemo(() => {
        return [...sourceHistoryMonths]
            .filter((month) => /^\d{4}-\d{2}$/.test(month))
            .sort((a, b) => b.localeCompare(a));
    }, [sourceHistoryMonths]);

    useEffect(() => {
        setSelectedSourceMonth((prevSelectedMonth) => {
            if (sortedSourceHistoryMonths.length === 0) {
                return prevSelectedMonth || currentMonth;
            }

            if (prevSelectedMonth && sortedSourceHistoryMonths.includes(prevSelectedMonth)) {
                return prevSelectedMonth;
            }

            return sortedSourceHistoryMonths[0];
        });
    }, [sortedSourceHistoryMonths, currentMonth]);

    const selectedSourceMonthDisplay = useMemo(() => {
        if (!selectedSourceMonth || !/^\d{4}-\d{2}$/.test(selectedSourceMonth)) {
            return `${monthName} ${year}`;
        }

        const [selectedYear, selectedMonthNumber] = selectedSourceMonth.split('-');
        const monthDate = new Date(Number(selectedYear), Number(selectedMonthNumber) - 1, 1);
        return monthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }, [selectedSourceMonth, monthName, year]);

    const {
        data: currentMonthUploadedFiles = [],
        isLoading: isLoadingCurrentMonthFiles,
        refetch: refetchCurrentMonthSourceFiles,
    } = useGetUploadedFilesInOrganizationQuery({ organizationId });
    const {
        data: selectedMonthUploadedFiles = [],
        isLoading: isLoadingSelectedMonthFiles,
        refetch: refetchSelectedMonthSourceFiles,
    } = useGetUploadedFilesInOrganizationQuery(
        { organizationId, month: selectedSourceMonth }
    );
    const {
        data: uploadedAugmentationFiles = [],
        isLoading: isLoadingAugmentationFiles,
        refetch: refetchAugmentationFiles,
    } = useGetAugmentationFilesInOrganizationQuery(organizationId);
    const [uploadFile, { isLoading }] = useUploadSourceFileMutation();
    const [uploadAugmentationFile, { isLoading: isLoadingAugmentation }] = useUploadAugmentationFileMutation();
    const [files, setFiles] = useState([]);
    const [pendingFileMetadata, setPendingFileMetadata] = useState(null);
    const [sourceHeaderError, setSourceHeaderError] = useState(null);
    const [augmentationFiles, setAugmentationFiles] = useState([]);
    const [pendingAugmentationFileMetadata, setPendingAugmentationFileMetadata] = useState(null);
    const [augmentationHeaderError, setAugmentationHeaderError] = useState(null);
    const [isAugmentationCollapsed, setIsAugmentationCollapsed] = useState(true);
    const [isSourceCollapsed, setIsSourceCollapsed] = useState(true);
    const [isSelectedSourceCollapsed, setIsSelectedSourceCollapsed] = useState(false);
    const [enhancedSourceFiles, setEnhancedSourceFiles] = useState([]);
    const [enhancedSelectedSourceFiles, setEnhancedSelectedSourceFiles] = useState([]);
    const [enhancedAugmentationFiles, setEnhancedAugmentationFiles] = useState([]);
    const [dateConfirmationOpen, setDateConfirmationOpen] = useState(false);
    const [pendingFileForDateConfirmation, setPendingFileForDateConfirmation] = useState(null);
    const [pendingHandlerForDateConfirmation, setPendingHandlerForDateConfirmation] = useState(null);
    const [pendingFileMonth, setPendingFileMonth] = useState(null);
    const [pendingFileYear, setPendingFileYear] = useState(null);
    const [isCurrentSourceHeadersLoading, setIsCurrentSourceHeadersLoading] = useState(false);
    const [isSelectedSourceHeadersLoading, setIsSelectedSourceHeadersLoading] = useState(false);

    const isOrganizationOnboarded = ORG_EXPECTED_FILES_WITH_HEADERS[organizationId] !== undefined;

    // Handle date confirmation dialog
    const handleDateConfirmation = (confirmed) => {
        setDateConfirmationOpen(false);
        if (confirmed && pendingHandlerForDateConfirmation && pendingFileForDateConfirmation) {
            pendingHandlerForDateConfirmation(pendingFileForDateConfirmation);
        }
        setPendingFileForDateConfirmation(null);
        setPendingHandlerForDateConfirmation(null);
    };

    // Combine uploaded files with pending file for source files
    const allSourceFiles = useMemo(() => {
        const baseFiles = enhancedSourceFiles.length > 0 ? enhancedSourceFiles : currentMonthUploadedFiles;
        const combinedFiles = [...baseFiles];
        if (pendingFileMetadata && !combinedFiles.find(f => f.file_name === pendingFileMetadata.file_name)) {
            combinedFiles.push(pendingFileMetadata);
        }
        return combinedFiles;
    }, [currentMonthUploadedFiles, enhancedSourceFiles, pendingFileMetadata]);

    const allSelectedMonthSourceFiles = useMemo(() => {
        const baseFiles = enhancedSelectedSourceFiles.length > 0 ? enhancedSelectedSourceFiles : selectedMonthUploadedFiles;
        return [...baseFiles];
    }, [enhancedSelectedSourceFiles, selectedMonthUploadedFiles]);

    useEffect(() => {
        // Clear card-local enriched state when switching months so new query data is shown.
        setEnhancedSelectedSourceFiles([]);
    }, [selectedSourceMonth]);

    // Combine uploaded files with pending file for augmentation files
    const allAugmentationFiles = useMemo(() => {
        const baseFiles = enhancedAugmentationFiles.length > 0 ? enhancedAugmentationFiles : uploadedAugmentationFiles;
        const files = [...baseFiles];
        return files;
    }, [uploadedAugmentationFiles, enhancedAugmentationFiles]);

    const toggleAugmentationCollapse = () => {
        setIsAugmentationCollapsed(prev => !prev);
    };

    const toggleSourceCollapse = () => {
        setIsSourceCollapsed(prev => !prev);
    };

    const toggleSelectedSourceCollapse = () => {
        setIsSelectedSourceCollapsed(prev => !prev);
    };

    const isSourceHeaderLoading = isCurrentSourceHeadersLoading || isSelectedSourceHeadersLoading;

    // Wrapper for file handlers that includes date validation
    const handleFileChangeWithDateValidation = async (newFiles, originalHandler) => {
        if (newFiles.length === 0) {
            originalHandler(newFiles);
            return;
        }

        const file = newFiles[0];
        const validation = validateFileDate(file.name, organizationId, currentMonth);

        if (!validation.isValid) {
            setSourceHeaderError(validation.error);
            setFiles([]);
            setPendingFileMetadata(null);
            return;
        }

        if (validation.isCurrentMonth === false) {
            // File date is not current month, show confirmation dialog
            setPendingFileForDateConfirmation(newFiles);
            setPendingHandlerForDateConfirmation(() => originalHandler);
            setPendingFileMonth(validation.fileDate.toLocaleDateString('en-US', { month: 'long' }));
            setPendingFileYear(validation.fileDate.getFullYear());
            setDateConfirmationOpen(true);
            return;
        }

        // File date is current month or no date validation needed
        originalHandler(newFiles);
    };

    // Custom handler for source file changes with header validation
    const handleSourceFilesChange = async (newFiles) => {
        const originalHandler = async (files) => {
            await handleFileChange(files, {
                fileType: FileType.SOURCE,
                organizationId,
                setFiles,
                setPendingFileMetadata,
                setHeaderError: setSourceHeaderError,
                errorMessage: 'The headers in this file do not match any expected file format for this organization. Please select a file with headers that match one of the expected file types. Options are:\n\n' + Object.entries(ORG_EXPECTED_FILES_WITH_HEADERS[organizationId] || {}).map(([fileType, headers]) => `- ${fileType}: ${headers.join(', ')}`).join('\n')
            });
        };
        
        await handleFileChangeWithDateValidation(newFiles, originalHandler);
    };

    // Custom handler for augmentation file changes with header validation
    const handleAugmentationFilesChange = async (newFiles) => {
        console.log('All augmentation files:', allAugmentationFiles);
        console.log('All source files:', allSourceFiles);
        await handleFileChange(newFiles, {
            fileType: FileType.AUGMENTATION,
            existingFiles: allAugmentationFiles,
            setFiles: setAugmentationFiles,
            setPendingFileMetadata: setPendingAugmentationFileMetadata,
            setHeaderError: setAugmentationHeaderError,
            errorMessage: 'The headers in this file do not match the headers in existing augmentation files. It must match the headers of at least one existing file. Please select a file with matching headers to one of these:\n\n' + allAugmentationFiles.map(f => `- ${f.header}`).join('\n')
        });
    };

    async function handleFileSubmit() {
        if (sourceHeaderError || files.length === 0) {
            return;
        }

        const formData = new FormData();
        formData.append('data', files[0]);
        
        try {
            await uploadFile({ data: formData, organizationId: organizationId }).unwrap();
            setFiles([]);
            setPendingFileMetadata(null);
            setEnhancedSourceFiles([]);
            setEnhancedSelectedSourceFiles([]);
            refetchCurrentMonthSourceFiles();
            refetchSelectedMonthSourceFiles();
        } catch (error) {
            console.error('Source file upload failed:', error);
        }
    }

    async function handleAugmentationFileSubmit() {
        if (augmentationHeaderError || augmentationFiles.length === 0) {
            return;
        }

        // Find the existing augmentation file whose headers match the uploaded file
        let fileToUpload = null;
        if (pendingAugmentationFileMetadata && pendingAugmentationFileMetadata.header) {
            // Try to find a file in allAugmentationFiles with the same header
            const match = allAugmentationFiles.find(f => f.header === pendingAugmentationFileMetadata.header && f.file_name);
            if (match && match.file_name) {
                fileToUpload = match;
            }
        }
        if (!fileToUpload) {
            setAugmentationHeaderError('No existing augmentation file with matching headers was found. Please check your file and try again.');
            return;
        }

        // Use the filename from fileToUpload, but the file contents from augmentationFiles[0]
        const fileContent = augmentationFiles[0];
        const formData = new FormData();
        formData.append('data', fileContent, fileToUpload.file_name);

        try {
            await uploadAugmentationFile({ data: formData, organizationId: organizationId }).unwrap();
            setAugmentationFiles([]);
            setPendingAugmentationFileMetadata(null);
            setEnhancedAugmentationFiles([]);
            refetchAugmentationFiles();
        } catch (error) {
            console.error('Augmentation file upload failed:', error);
        }
    }

    return (
        <Stack pl={4} pr={4} spacing={3}>
            <Stack spacing={1}>
                <Typography variant='heavy' fontSize='24px' sx={{ marginTop: '20px' }}>
                    <strong>Upload Files</strong>
                </Typography>
                <Typography>
                    Organization: <strong>{organization?.name}</strong>
                </Typography>
            </Stack>
            
            {!isOrganizationOnboarded ? (
                <Alert severity="warning" mt={2}>
                    <Typography variant="heavy" sx={{ mb: 1 }}>
                        Organization Onboarding Required
                    </Typography>
                    <Typography>
                        This organization needs to be onboarded to automatic transformation & ingestion before files can be uploaded. 
                        Please contact your administrator to configure the expected file formats for this organization.
                    </Typography>
                </Alert>
            ) : (
                <>
                    {canUploadSourceFiles && sourceHeaderError && (
                        <Alert severity="error" mt={1}>
                            <Typography sx={{ whiteSpace: 'pre-line' }}>
                                {sourceHeaderError}
                            </Typography>
                        </Alert>
                    )}
                    {augmentationHeaderError && (
                        <Alert severity="error" mt={1}>
                            <Typography sx={{ whiteSpace: 'pre-line' }}>
                                {augmentationHeaderError}
                            </Typography>
                        </Alert>
                    )}
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="stretch">
                        <ExpectedFilesList organizationId={organizationId} />
                        {canUploadSourceFiles && (
                            <FileUploadSection
                                fileType="Source"
                                files={files}
                                onFilesChange={handleSourceFilesChange}
                                onPendingFileChange={setPendingFileMetadata}
                                onFileSubmit={handleFileSubmit}
                                isLoading={isLoading}
                            />
                        )}
                        <FileUploadSection 
                            fileType="Augmentation"
                            files={augmentationFiles}
                            onFilesChange={handleAugmentationFilesChange}
                            onPendingFileChange={setPendingAugmentationFileMetadata}
                            onFileSubmit={handleAugmentationFileSubmit}
                            isLoading={isLoadingAugmentation}
                        />
                    </Stack>
                    
                    <Typography variant='heavy' fontSize='18px'>
                        Current Augmentation Files
                    </Typography>
                    {isLoadingAugmentationFiles ? (
                        <Stack spacing={2} alignItems="center">
                            <CircularProgress />
                        </Stack>
                    ) : allAugmentationFiles.length > 0 ? (
                        <FileGroupingCard
                            month="augmentation"
                            files={allAugmentationFiles}
                            isCollapsed={isAugmentationCollapsed}
                            onToggleMonth={toggleAugmentationCollapse}
                            cardTitle='Augmentation Files'
                            pendingReplacement={pendingAugmentationFileMetadata}
                            organizationId={organizationId}
                            fileType="augmentation"
                            onFilesUpdate={setEnhancedAugmentationFiles}
                        />
                    ) : null}
                    
                    <Typography variant='heavy' fontSize='18px'>
                        This Month's Uploaded Source Files
                    </Typography>
                    {isLoadingCurrentMonthFiles ? (
                        <Stack spacing={2} alignItems="center">
                            <CircularProgress />
                        </Stack>
                    ) : allSourceFiles.length > 0 ? (
                        <FileGroupingCard
                            month={currentMonth}
                            files={allSourceFiles}
                            isCollapsed={isSourceCollapsed}
                            onToggleMonth={toggleSourceCollapse}
                            cardTitle='Source Files'
                            pendingReplacement={pendingFileMetadata}
                            organizationId={organizationId}
                            fileType="source"
                            onFilesUpdate={setEnhancedSourceFiles}
                            onHeaderLoadingChange={setIsCurrentSourceHeadersLoading}
                        />
                    ) : (
                        <Typography variant="body1" sx={{ 
                            fontStyle: 'italic', 
                            color: 'text.secondary',
                            textAlign: 'center',
                            py: 1
                        }}>
                            No customer files uploaded in the month of: {monthName} {year}, or the files have{' '}
                            <Tooltip title="Check the Transformation Jobs section below - if a job has run this month, it means customer files have already been processed" arrow>
                                <span style={{ textDecoration: 'underline', cursor: 'help' }}>
                                    already been processed
                                </span>
                            </Tooltip>
                        </Typography>
                    )}

                    <Typography variant='heavy' fontSize='18px'>
                        Source Files for {selectedSourceMonthDisplay}
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                            <InputLabel id="source-file-month-label">Source File Month</InputLabel>
                            <Select
                                labelId="source-file-month-label"
                                value={selectedSourceMonth || currentMonth}
                                label="Source File Month"
                                onChange={(event) => setSelectedSourceMonth(event.target.value)}
                                disabled={isLoadingSourceHistoryMonths || sortedSourceHistoryMonths.length === 0 || isSourceHeaderLoading}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            maxHeight: 300,
                                        },
                                    },
                                }}
                            >
                                {sortedSourceHistoryMonths.map((month) => (
                                    <MenuItem key={month} value={month}>
                                        {month}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {isSourceHeaderLoading && <CircularProgress size={20} />}
                    </Stack>
                    {isLoadingSelectedMonthFiles ? (
                        <Stack spacing={2} alignItems="center">
                            <CircularProgress />
                        </Stack>
                    ) : allSelectedMonthSourceFiles.length > 0 ? (
                        <FileGroupingCard
                            month={selectedSourceMonth}
                            files={allSelectedMonthSourceFiles}
                            isCollapsed={isSelectedSourceCollapsed}
                            onToggleMonth={toggleSelectedSourceCollapse}
                            cardTitle={`Source Files (${selectedSourceMonth})`}
                            pendingReplacement={null}
                            organizationId={organizationId}
                            fileType="source"
                            onFilesUpdate={setEnhancedSelectedSourceFiles}
                            onHeaderLoadingChange={setIsSelectedSourceHeadersLoading}
                            historyDate={selectedSourceMonth}
                        />
                    ) : (
                        <Typography variant="body1" sx={{ 
                            fontStyle: 'italic', 
                            color: 'text.secondary',
                            textAlign: 'center',
                            py: 1
                        }}>
                            No customer files uploaded in the month of: {selectedSourceMonthDisplay}.
                        </Typography>
                    )}
                    
                    <Stack pb={4}>
                        <TransformationJobs organizationId={organizationId} />
                    </Stack>
                </>
            )}
            
            {/* Date Confirmation Dialog */}
            <Dialog open={dateConfirmationOpen} onClose={() => handleDateConfirmation(false)}>
                <DialogTitle>Confirm File Date</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        The selected file appears to contain data from a different month than the current month ({monthName} {year} vs {pendingFileMonth} {pendingFileYear}). <br />
                        It may not get processed in the current transformation cycle. Are you sure you want to upload this file?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleDateConfirmation(false)} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={() => handleDateConfirmation(true)} color="primary" variant="contained">
                        Upload Anyway
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}

export default UploadSourceFile;
